const BREAKPOINTS = {
  mobile: 990,
  scrollThreshold: 120,
  hoverDelay: 200
};

const isMobileViewport = () => window.innerWidth < BREAKPOINTS.mobile;
const getResponsiveHeight = (mobileHeight, desktopHeight) => 
  isMobileViewport() ? mobileHeight : desktopHeight;
class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.announcementHeight = parseInt(this.dataset.announcementHeight, 10) || 0;
    this.mobileHeight = parseInt(this.dataset.mobileHeight, 10) || 0;
    this.desktopHeight = parseInt(this.dataset.desktopHeight, 10) || 0;
  }

  connectedCallback() {
    this.header = this.querySelector('header');
    this.headerIsAlwaysSticky =
      this.getAttribute('data-sticky-type') === 'always' ||
      this.getAttribute('data-sticky-type') === 'reduce-logo-size';
    this.debouncedUpdate = window.debounce(this.updateHeaderHeights.bind(this), 100);

    this.mediaQuery = window.matchMedia(`(max-width: ${BREAKPOINTS.mobile}px)`);
    this.mediaQuery.addEventListener('change', this.debouncedUpdate);

    this.resizeObserver = new ResizeObserver((entries) => {
      if (entries.some((entry) => entry.contentRect)) {
        this.debouncedUpdate();
      }
    });
    this.debouncedUpdate();
    if (this.header) this.resizeObserver.observe(this.header);
    const announcementBar = document.querySelector('.announcement-bar-section');
    if (announcementBar) this.resizeObserver.observe(announcementBar);
    this.resizeObserver.observe(document.documentElement);
    this.onScrollHandler = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });

    if (this.headerIsAlwaysSticky) {
      document.documentElement.classList.add('header-sticky--active');
    }
    this.currentScrollTop = 0;
    this.preventReveal = false;
  }

  updateHeaderHeights() {
    if (this._updating) return;
    this._updating = true;

    requestAnimationFrame(() => {
      try {
        const root = document.documentElement;
        const announcementBar = document.querySelector('.announcement-bar-section');
        const header = this.header;
        let announcementHeight = this.announcementHeight;
        let headerHeight = getResponsiveHeight(this.mobileHeight, this.desktopHeight);
        if (announcementBar) {
          announcementHeight = Math.max(
            this.announcementHeight,
            Math.ceil(announcementBar.getBoundingClientRect().height),
          );
          root.style.setProperty('--announcement-bar-height', `${announcementHeight}px`);
        }

        if (header) {
          const minHeight = getResponsiveHeight(this.mobileHeight, this.desktopHeight);
          headerHeight = Math.max(minHeight, Math.ceil(header.getBoundingClientRect().height));
          root.style.setProperty('--header-height', `${headerHeight}px`);
        }
        if (announcementHeight !== undefined && headerHeight !== undefined) {
          const genderTabsHeight = parseInt(
            getComputedStyle(root).getPropertyValue('--gender-tabs-height') || '0',
            10
          );
          const combinedHeight = announcementHeight + headerHeight + genderTabsHeight;
          root.style.setProperty('--combined-header-height', `${combinedHeight}px`);
          window.dispatchEvent(
            new CustomEvent('header:height-change', {
              detail: {
                announcementHeight,
                headerHeight,
                genderTabsHeight,
                combinedHeight,
              },
            }),
          );
        }
      } catch (error) {
        console.error('Error updating header heights:', error);
      } finally {
        this._updating = false;
      }
    });
  }

  onScroll() {
    const searchModal = document.querySelector('.header__search details[open]');
    if (document.documentElement.classList.contains('main-nav-panel-open') || searchModal) return;
    if (this.headerIsAlwaysSticky) {
      document.documentElement.classList.add('header-sticky--active');
      return;
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop > this.currentScrollTop && scrollTop > BREAKPOINTS.scrollThreshold) {
      document.documentElement.classList.add('header-sticky--hidden');
    } else if (scrollTop < this.currentScrollTop && scrollTop > BREAKPOINTS.scrollThreshold) {
      document.documentElement.classList.add('header-sticky--active');
      document.documentElement.classList.remove('header-sticky--hidden');
    } else if (scrollTop <= BREAKPOINTS.scrollThreshold) {
      document.documentElement.classList.remove('header-sticky--hidden', 'header-sticky--active');
    }

    this.currentScrollTop = scrollTop;
  }

  disconnectedCallback() {
    this.mediaQuery.removeEventListener('change', this.debouncedUpdate);
    window.removeEventListener('scroll', this.onScrollHandler);
    this.resizeObserver.disconnect();
  }
}

let abortController = new AbortController();

const updateAriaExpanded = (element, isExpanded) => {
  if (element) element.setAttribute('aria-expanded', isExpanded);
};

const toggleNavState = (state, isOpen) => {
  document.documentElement.classList.toggle(state, isOpen);
};

const initHeaderNav = () => {
  abortController.abort();
  abortController = new AbortController();
  const { signal } = abortController;
  const isMobile = isMobileViewport();
  
  const menu = document.querySelector('.main-nav-v2');
  const mobileMenuToggle = document.querySelector('.mobile-menu-toggle');
  const panelOpen = menu?.querySelector('.panel-open');
  const flyoutClose = menu?.querySelector('.flyout-close');
  const flyoutTriggers = document.querySelectorAll('[data-flyout-trigger]');
  const allNavLinks = document.querySelectorAll('.main-nav-v2 > ul > li > .link');
  const flyouts = document.querySelectorAll('.main-nav-v2 .flyout');

  let activeFlyout = null;
  let hoverTimeout = null;

  const openFlyout = (targetId) => {
    if (activeFlyout) closeFlyouts();
    const flyout = document.querySelector(`#flyout-${targetId}`);
    const trigger = document.querySelector(`[data-flyout-trigger='${targetId}']`);
    if (flyout && trigger) {
      flyout.classList.add('flyout--active');
      trigger.setAttribute('aria-expanded', 'true');
      trigger.closest('li')?.classList.add('active');
      activeFlyout = flyout;
    }
  };

  const closeFlyouts = () => {
    flyouts.forEach((flyout) => flyout.classList.remove('flyout--active'));
    document.querySelectorAll('.main-nav-v2 .item.active').forEach((item) => {
      item.classList.remove('active');
      const trigger = item.querySelector('[data-flyout-trigger]');
      updateAriaExpanded(trigger, 'false');
    });
    updateAriaExpanded(mobileMenuToggle, 'false');
    activeFlyout = null;
  };

  // --- Global Listeners ---
  const handleFlyoutClose = (e) => {
    e.preventDefault();
    closeFlyouts();
    document.documentElement.classList.remove('main-nav-cat-open', 'main-nav-panel-open');
    updateAriaExpanded(mobileMenuToggle, 'false');
  };

  const handleMobileMenuToggle = () => {
    const isOpen = document.documentElement.classList.toggle('main-nav-panel-open');
    document.documentElement.classList.remove('header-sticky--hidden', 'main-nav-cat-open');
    if (!isOpen) {
      closeFlyouts();
    }
    updateAriaExpanded(mobileMenuToggle, isOpen);
  };

  const handlePanelOpen = (e) => {
    e.preventDefault();
    toggleNavState('main-nav-panel-open', true);
    toggleNavState('main-nav-cat-open', false);
    closeFlyouts();
    updateAriaExpanded(mobileMenuToggle, 'true');
  };

  flyoutClose?.addEventListener('click', handleFlyoutClose, { signal });
  mobileMenuToggle?.addEventListener('click', handleMobileMenuToggle, { signal });
  panelOpen?.addEventListener('click', handlePanelOpen, { signal });

  if (isMobile) {
    setTimeout(() => {
      const activeItem = document.querySelector('.main-nav-v2 .active');
      if (activeItem) {
        activeItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  } else {
    const createOpenHandler = (trigger) => () => {
      clearTimeout(hoverTimeout);
      hoverTimeout = setTimeout(() => {
        openFlyout(trigger.getAttribute('data-flyout-trigger'));
      }, BREAKPOINTS.hoverDelay);
    };

    const createCloseHandler = () => () => {
      clearTimeout(hoverTimeout);
    };

    flyoutTriggers.forEach((trigger) => {
      const openHandler = createOpenHandler(trigger);
      const closeHandler = createCloseHandler();

      trigger.addEventListener('mouseenter', openHandler, { signal });
      trigger.addEventListener('focusin', openHandler, { signal });
      trigger.addEventListener('mouseleave', closeHandler, { signal });
      trigger.addEventListener('focusout', closeHandler, { signal });
    });

    allNavLinks.forEach((link) => {
      if (!link.hasAttribute('data-flyout-trigger')) {
        link.addEventListener('mouseenter', closeFlyouts, { signal });
      }
    });

    flyouts.forEach((flyout) => {
      flyout.addEventListener('mouseleave', closeFlyouts, { signal });
      flyout.addEventListener(
        'focusout',
        (e) => {
          if (!flyout.contains(e.relatedTarget)) closeFlyouts();
        },
        { signal },
      );
    });
  }
};

let resizeListenerAdded = false;

export const headerComponent = {
  el: '.header-component',
  init() {
    if (!customElements.get('sticky-header')) {
      customElements.define('sticky-header', StickyHeader);
    }

    const searchModalDetails = document.querySelector('.header__search details');
    if (searchModalDetails) {
      searchModalDetails.addEventListener('toggle', (event) => {
        if (event.target.open) {
          document.body.classList.add('no-scroll');
        } else {
          document.body.classList.remove('no-scroll');
        }
      });
    }

    initHeaderNav();

    if (!resizeListenerAdded) {
      let wasMobile = isMobileViewport();
      const debouncedNavInit = window.debounce(() => {
        const isMobile = isMobileViewport();
        if (isMobile !== wasMobile) {
          wasMobile = isMobile;
          initHeaderNav();
        }
      }, 200);

      window.addEventListener('resize', debouncedNavInit);
      resizeListenerAdded = true;
    }
  },
};
